import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CategorySpendBars - Stacked bar chart for spend by category with vendor segments
 *
 * Shows spend per category (e.g., security product categories) broken down by vendor.
 * Presentational component - data via props, theme via CSS variables.
 *
 * Features:
 * - Stacked bars showing ALL vendors (no "Other" bucket)
 * - Stable colors: same vendor = same color across all bars
 * - Categories sorted by total spend (descending)
 * - Smart flipping tooltip that lists EVERY vendor segment
 * - Scrollable legend for many vendors
 * - Segment drilldown for vendor × category
 * - Min-height for tiny segments to keep them hoverable
 */
import { useState, useMemo, useRef, useCallback } from 'react';
import { Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ComposedChart, } from 'recharts';
import { chartPaletteExtended, chartOtherColor } from '../tokens.js';
import { ChartDrillList } from './ChartDrillList.js';
/*
 * The palette is imported, never restated. This file used to carry its own
 * 24-colour copy; four other files carried the same first twelve, and that is
 * how the ramp came to disagree with the `chartPalette` token it was supposed
 * to be. Slot n IS series n — see tokens.ts.
 */
const VENDOR_PALETTE = chartPaletteExtended;
/** Vendors past the end of the ramp, and unattributed spend, are a residual. */
const FALLBACK_COLOR = chartOtherColor;
/** Format currency for display */
const formatCurrency = (value) => {
    if (value >= 1000000000)
        return `$${(value / 1000000000).toFixed(1)}B`;
    if (value >= 1000000)
        return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000)
        return `$${(value / 1000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
};
/** Custom tooltip */
function CustomTooltip({ rows, title, total, hoveredVendor, }) {
    return (_jsxs("div", { className: "cht", children: [_jsx("div", { className: "cht__title", children: title }), _jsx("div", { className: "cht__rows", children: rows.map((row) => {
                    const pct = total > 0 ? ((row.value / total) * 100).toFixed(0) : '0';
                    return (_jsxs("div", { className: `cht__row${row.vendor === hoveredVendor ? ' is-hovered' : ''}`, style: { '--swatch': row.color }, children: [_jsx("span", { className: "cht__swatch" }), _jsx("span", { className: "cht__name", title: row.vendor, children: row.vendor }), _jsxs("span", { className: "cht__meta", children: [_jsxs("span", { className: "cht__pct", children: [pct, "%"] }), _jsx("span", { className: "cht__val", children: formatCurrency(row.value) })] })] }, row.vendor));
                }) }), _jsxs("div", { className: "cht__total", children: [_jsx("span", { children: "Total" }), _jsx("span", { className: "cht__val", children: formatCurrency(total) })] })] }));
}
/** Custom legend - scrollable for many vendors */
function CustomLegend({ vendors, colorMap, selectedVendor, hoveredVendor, onVendorClick, onVendorHover, }) {
    return (_jsx("div", { className: "chl chl--list", children: vendors.map((vendor) => {
            const isSelected = selectedVendor === vendor;
            const isHovered = hoveredVendor === vendor;
            const isDimmed = (selectedVendor && !isSelected) || (hoveredVendor && !isHovered && !isSelected);
            return (_jsxs("button", { type: "button", onClick: () => onVendorClick?.(vendor), onMouseEnter: () => onVendorHover(vendor), onMouseLeave: () => onVendorHover(null), className: `chl__item${isSelected ? ' is-selected' : ''}${isHovered ? ' is-hovered' : ''}${isDimmed ? ' is-dimmed' : ''}`, style: { '--swatch': colorMap[vendor] || FALLBACK_COLOR }, children: [_jsx("span", { className: "chl__swatch" }), vendor] }, vendor));
        }) }));
}
export function CategorySpendBars({ data, topVendors, colorMap: propColorMap, height = 320, fill = false, maxBarSize = 64, barCategoryGap = '20%', onVendorClick, selectedVendor, onSegmentClick, title, subtitle, className, hideEmpty = true, }) {
    const [hoveredVendor, setHoveredVendor] = useState(null);
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [tooltipPos, setTooltipPos] = useState(null);
    const chartContainerRef = useRef(null);
    // Sort data by totalSpend descending and filter empty if needed
    const sortedData = useMemo(() => {
        let filtered = hideEmpty ? data.filter((d) => d.totalSpend > 0) : data;
        return [...filtered].sort((a, b) => b.totalSpend - a.totalSpend);
    }, [data, hideEmpty]);
    // Calculate GLOBAL vendor totals (across all categories) for:
    // 1. Stable color assignment (same vendor = same color across all bars)
    // 2. Stack ordering: biggest global vendor at BOTTOM of every bar
    const vendorGlobalTotals = useMemo(() => {
        const totals = {};
        data.forEach((cat) => {
            Object.entries(cat.vendors).forEach(([vendor, spend]) => {
                totals[vendor] = (totals[vendor] || 0) + spend;
            });
        });
        return totals;
    }, [data]);
    // Vendors sorted by GLOBAL total spend DESCENDING (biggest first)
    // In Recharts stacking, first-rendered Bar = bottom of stack
    // So this order puts biggest vendors at the bottom of every bar
    const allVendors = useMemo(() => {
        if (topVendors)
            return topVendors;
        return Object.entries(vendorGlobalTotals)
            .sort((a, b) => b[1] - a[1]) // Descending by global total
            .map(([vendor]) => vendor);
    }, [vendorGlobalTotals, topVendors]);
    // Build color map - stable colors based on global vendor order
    const colorMap = useMemo(() => {
        if (propColorMap)
            return propColorMap;
        const map = {};
        allVendors.forEach((vendor, idx) => {
            map[vendor] = idx < VENDOR_PALETTE.length
                ? VENDOR_PALETTE[idx]
                : FALLBACK_COLOR;
        });
        return map;
    }, [propColorMap, allVendors]);
    // Build chart data with ALL vendor segments (no "Other" bucket)
    const { chartData, stackKeys } = useMemo(() => {
        const transformed = sortedData.map((cat) => {
            const row = {
                category: cat.category,
                categoryId: cat.categoryId,
                totalSpend: cat.totalSpend,
            };
            // Initialize all vendors to 0
            allVendors.forEach((v) => {
                row[v] = 0;
            });
            // Populate vendor values directly (no "Other" grouping)
            Object.entries(cat.vendors).forEach(([vendor, spend]) => {
                if (vendor in row) {
                    row[vendor] = row[vendor] + spend;
                }
                else {
                    // Vendor not in allVendors list (shouldn't happen if topVendors is correct)
                    row[vendor] = spend;
                }
            });
            return row;
        });
        // All vendors are stack keys (no "Other" bucket)
        return { chartData: transformed, stackKeys: allVendors };
    }, [sortedData, allVendors]);
    // Tooltip rows for hovered category
    const tooltipRows = useMemo(() => {
        if (!hoveredSegment)
            return [];
        const catData = chartData.find((d) => d.category === hoveredSegment.category);
        if (!catData)
            return [];
        return stackKeys
            .map((vendor) => ({
            vendor,
            value: catData[vendor] || 0,
            color: colorMap[vendor] || FALLBACK_COLOR,
        }))
            .filter((r) => r.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [hoveredSegment, chartData, stackKeys, colorMap]);
    const tooltipTotal = useMemo(() => {
        if (!hoveredSegment)
            return 0;
        const catData = chartData.find((d) => d.category === hoveredSegment.category);
        return catData ? catData.totalSpend : 0;
    }, [hoveredSegment, chartData]);
    // Handle segment click (all vendors are drillable, including "Various")
    const handleSegmentClick = useCallback((vendor, payload) => {
        if (!onSegmentClick)
            return;
        onSegmentClick({
            vendor,
            categoryId: payload.categoryId,
            category: payload.category,
            spend: payload[vendor] || 0,
        });
    }, [onSegmentClick]);
    /*
     * One entry per DRAWN segment, in the order the bars are stacked, so tabbing
     * walks the chart left to right the way reading it does. A zero-spend
     * vendor/category pair draws nothing, so it gets no control either —
     * otherwise the tab order fills with segments that are not on screen.
     */
    const drillTargets = useMemo(() => {
        const out = [];
        for (const entry of chartData) {
            for (const vendor of stackKeys) {
                const spend = entry[vendor];
                if (!spend)
                    continue;
                out.push({
                    vendor,
                    category: entry.category,
                    payload: entry,
                    label: formatCurrency(spend),
                });
            }
        }
        return out;
    }, [chartData, stackKeys]);
    // Compute tooltip position
    const computeTooltipPos = useCallback((barIdx, totalBars) => {
        const container = chartContainerRef.current;
        if (!container)
            return { left: 100, top: 40 };
        const containerWidth = container.offsetWidth;
        const chartLeft = 65; // Y-axis width
        const chartRight = 8;
        const chartWidth = containerWidth - chartLeft - chartRight;
        const barWidth = chartWidth / totalBars;
        const barCenterX = chartLeft + barIdx * barWidth + barWidth / 2;
        // Flip tooltip to left side if bar is in right half
        const tooltipWidth = 220;
        const left = barCenterX > containerWidth / 2
            ? barCenterX - tooltipWidth - 15
            : barCenterX + 15;
        return { left: Math.max(10, Math.min(left, containerWidth - tooltipWidth - 10)), top: 40 };
    }, []);
    // Get opacity for bar segment
    const getOpacity = useCallback((vendor, category) => {
        // Full opacity if nothing is hovered/selected
        if (!hoveredVendor && !selectedVendor && !hoveredSegment)
            return 1;
        // Selected vendor takes precedence
        if (selectedVendor) {
            return vendor === selectedVendor ? 1 : 0.25;
        }
        // Hovered segment
        if (hoveredSegment) {
            return hoveredSegment.category === category && hoveredSegment.vendor === vendor
                ? 1
                : hoveredSegment.vendor === vendor
                    ? 0.7
                    : 0.35;
        }
        // Hovered vendor in legend
        if (hoveredVendor) {
            return vendor === hoveredVendor ? 1 : 0.35;
        }
        return 1;
    }, [hoveredVendor, selectedVendor, hoveredSegment]);
    if (sortedData.length === 0) {
        return (_jsx("div", { className: `chs__empty${className ? ` ${className}` : ''}`, children: "No category spend data available" }));
    }
    /*
     * `.ts-chart-fill` marks a chart's OUTERMOST box — that is the contract the
     * one rule in account-board.css is written against. This component put it on
     * the inner chart box instead and left the wrapper an unflexed block, so the
     * inner `height: 100%` resolved against an auto-height parent and collapsed:
     * on the account board the partner chart rendered as a 48-76px sliver with
     * only its legend visible. The wrapper flexes, and becomes a column so the
     * header keeps its natural height and the chart takes the rest.
     */
    const fillWrap = ['chs', className, fill ? 'chs--fill ts-chart-fill' : null].filter(Boolean).join(' ');
    return (_jsxs("div", { className: fillWrap, children: [(title || selectedVendor) && (_jsxs("div", { className: "chs__head", children: [_jsx("div", { children: title && _jsx("h3", { className: "chs__title", children: title }) }), _jsx("div", { className: "chs__controls", children: selectedVendor && (_jsxs("button", { type: "button", onClick: () => onVendorClick?.(selectedVendor), className: "chs__clear", children: [_jsxs("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), _jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }), "Clear filter"] })) })] })), subtitle && (_jsx("p", { className: "chs__subtitle", children: subtitle })), _jsxs("div", { ref: chartContainerRef, className: `chs__plot${fill ? ' chs__plot--fill' : ''}`, style: fill ? undefined : { '--h': `${height}px` }, children: [_jsx(ResponsiveContainer, { width: "100%", height: "100%", className: fill ? 'ts-chart-fill' : undefined, children: _jsxs(ComposedChart, { data: chartData, margin: { top: 8, right: 8, left: 0, bottom: sortedData.length > 6 ? 80 : 50 }, barCategoryGap: barCategoryGap, onMouseLeave: () => {
                                setHoveredSegment(null);
                                setTooltipPos(null);
                            }, children: [_jsx(CartesianGrid, { strokeDasharray: "3 3", stroke: "var(--color-border, #3f3f46)", vertical: false }), _jsx(XAxis, { dataKey: "category", axisLine: false, tickLine: false, tick: {
                                        fill: 'var(--color-text-muted, #a1a1aa)',
                                        fontSize: 11,
                                    }, interval: 0, angle: sortedData.length > 6 ? -45 : 0, textAnchor: sortedData.length > 6 ? 'end' : 'middle', height: sortedData.length > 6 ? 80 : 50 }), _jsx(YAxis, { axisLine: false, tickLine: false, tick: {
                                        fill: 'var(--color-text-muted, #a1a1aa)',
                                        fontSize: 12,
                                    }, tickFormatter: formatCurrency, width: 65 }), _jsx(Tooltip, { content: () => null, cursor: { fill: 'var(--color-surface-hover, rgba(255,255,255,0.04))' }, wrapperStyle: { display: 'none' } }), stackKeys.map((vendor, vendorIdx) => (_jsx(Bar, { dataKey: vendor, stackId: "vendors", fill: colorMap[vendor], radius: vendorIdx === stackKeys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0], cursor: onSegmentClick ? 'pointer' : 'default', isAnimationActive: false, maxBarSize: maxBarSize, minPointSize: 3, onClick: (d) => {
                                        if (d?.payload) {
                                            handleSegmentClick(vendor, d.payload);
                                        }
                                    }, children: chartData.map((entry, idx) => {
                                        const isHovered = hoveredSegment?.category === entry.category && hoveredSegment?.vendor === vendor;
                                        return (_jsx(Cell, { fill: colorMap[vendor], fillOpacity: getOpacity(vendor, entry.category), stroke: isHovered ? 'var(--color-text, #fff)' : 'transparent', strokeWidth: isHovered ? 1.5 : 0, onMouseEnter: () => {
                                                setHoveredSegment({ category: entry.category, vendor });
                                                setTooltipPos(computeTooltipPos(idx, chartData.length));
                                            } }, `${entry.category}-${vendor}`));
                                    }) }, vendor)))] }) }), hoveredSegment && tooltipPos && tooltipRows.length > 0 && (_jsx("div", { className: "chs__tip", style: { '--x': `${tooltipPos.left}px`, '--y': `${tooltipPos.top}px` }, children: _jsx(CustomTooltip, { rows: tooltipRows, title: hoveredSegment.category, total: tooltipTotal, hoveredVendor: hoveredSegment.vendor }) }))] }), stackKeys.length > 0 && (_jsx(CustomLegend, { vendors: stackKeys, colorMap: colorMap, selectedVendor: selectedVendor, hoveredVendor: hoveredVendor, onVendorClick: onVendorClick, onVendorHover: setHoveredVendor })), onSegmentClick && (_jsx(ChartDrillList, { label: `${title || 'Chart'} — open transactions by segment`, drills: drillTargets.map((d) => ({
                    key: `${d.category}-${d.vendor}`,
                    label: `${d.vendor}, ${d.category}${d.label ? `, ${d.label}` : ''} — open transactions`,
                    onSelect: () => handleSegmentClick(d.vendor, d.payload),
                })) }))] }));
}
export default CategorySpendBars;
